import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Loader2, Play, RotateCcw, CheckCircle, XCircle, Clock, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function MelissaDripPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/melissa/stats`);
      setStats(res.data);
      setLastRefreshedAt(Date.now());
    } catch (e) {
      console.error("Failed to load melissa stats", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/melissa/run-now`);
      if (res.data.status === "error") {
        toast.error(res.data.message);
      } else if (res.data.status === "limit_reached") {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
        // Refresh stats after a delay to allow processing
        setTimeout(() => fetchStats(), 5000);
      }
    } catch (e) {
      toast.error("Failed to trigger drip run");
    } finally {
      setRunning(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset the monthly credit counter to 0? This is an emergency action.")) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/melissa/reset-counter`);
      toast.success(res.data.message);
      fetchStats();
    } catch {
      toast.error("Failed to reset counter");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!stats) {
    return <p className="text-sm text-slate-400 text-center py-6">Failed to load stats</p>;
  }

  const progressPct = stats.remaining > 0
    ? Math.round(((stats.verified_all_time + stats.failed) / (stats.verified_all_time + stats.failed + stats.remaining)) * 100)
    : 100;

  const creditPct = Math.round((stats.credits_used_this_month / stats.credit_limit) * 100);

  return (
    <div className="space-y-4" data-testid="melissa-drip-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchStats} lastRefreshedAt={lastRefreshedAt} testId="melissa-refresh" /></div>
      {/* API Key Warning */}
      {!stats.has_api_key && (
        <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="melissa-no-key-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-800">API Key Not Configured</p>
            <p className="text-[10px] text-amber-600 mt-0.5">
              Add MELISSA_API_KEY to backend/.env and restart the backend to enable address verification.
            </p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2" data-testid="melissa-stats-grid">
        <StatBox label="Total Records" value={stats.total_records.toLocaleString()} color="text-slate-700" />
        <StatBox label="Meta Skipped" value={stats.meta_skipped.toLocaleString()} color="text-slate-400" />
        <StatBox label="Remaining" value={stats.remaining.toLocaleString()} color="text-sky-600" />
        <StatBox label="Verified (All)" value={stats.verified_all_time.toLocaleString()} color="text-emerald-600" icon={<CheckCircle className="w-3 h-3 text-emerald-500" />} />
        <StatBox label="Verified (Month)" value={stats.verified_this_month.toLocaleString()} color="text-emerald-600" />
        <StatBox label="Failed" value={stats.failed.toLocaleString()} color={stats.failed > 0 ? "text-red-600" : "text-slate-400"} icon={stats.failed > 0 ? <XCircle className="w-3 h-3 text-red-500" /> : null} />
      </div>

      {/* Overall Progress Bar */}
      <div data-testid="melissa-progress">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold text-slate-500">Overall Progress</span>
          <span className="text-[10px] font-bold text-slate-700">{progressPct}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, backgroundColor: progressPct === 100 ? "#10b981" : "#3b82f6" }}
          />
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          {stats.estimated_months_to_complete > 0
            ? `~${stats.estimated_months_to_complete} month${stats.estimated_months_to_complete !== 1 ? "s" : ""} to complete`
            : "All addresses processed"}
        </p>
      </div>

      {/* Monthly Credits Bar */}
      <div data-testid="melissa-credits">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-semibold text-slate-500">Credits This Month ({stats.month})</span>
          <span className="text-[10px] font-bold text-slate-700">{stats.credits_used_this_month.toLocaleString()} / {stats.credit_limit.toLocaleString()}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(creditPct, 100)}%`,
              backgroundColor: creditPct >= 100 ? "#f59e0b" : creditPct >= 80 ? "#f97316" : "#3b82f6",
            }}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2" data-testid="melissa-actions">
        <button
          onClick={handleRunNow}
          disabled={running || !stats.has_api_key || stats.credits_used_this_month >= stats.credit_limit}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="melissa-run-now-btn"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Run Now
        </button>
        <button
          onClick={handleReset}
          disabled={resetting}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200 transition disabled:opacity-40"
          data-testid="melissa-reset-btn"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
          Reset Counter
        </button>
      </div>

      {/* Recent Runs */}
      {stats.recent_runs && stats.recent_runs.length > 0 && (
        <div data-testid="melissa-recent-runs">
          <p className="text-[10px] font-semibold text-slate-500 mb-2">Recent Runs</p>
          <div className="space-y-1.5">
            {stats.recent_runs.map((run, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-[10px]"
                data-testid={`melissa-run-${i}`}
              >
                <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span className="text-slate-500">{run.run_date?.slice(0, 10)}</span>
                <span className="text-emerald-600 font-bold">{run.records_verified} verified</span>
                {run.records_failed > 0 && <span className="text-red-500 font-bold">{run.records_failed} failed</span>}
                <span className="text-slate-400 ml-auto">{run.duration_seconds}s</span>
                <span className="text-slate-400 capitalize">{run.triggered_by}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color = "text-slate-700", icon }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-2.5 shadow-sm">
      <div className="flex items-center gap-1 mb-0.5">
        {icon}
        <p className="text-[10px] text-slate-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-lg font-bold ${color} leading-tight`}>{value}</p>
    </div>
  );
}
